import TuneIcon from "@mui/icons-material/Tune";
import classNames from "classnames";
import { useEffect, useRef } from "react";
import styles from "./style.module.scss";

export interface BlockToggle {
  key: string;
  label: string;
  visible: boolean;
}

interface CustomizeViewMenuProps {
  blocks: BlockToggle[];
  open: boolean;
  onToggleOpen: (open: boolean) => void;
  onToggleBlock: (key: string, visible: boolean) => void;
  label?: string;
  className?: string;
}

/** "Customize view" dropdown that shows or hides individual analytics blocks. */
function CustomizeViewMenu({
  blocks,
  open,
  onToggleOpen,
  onToggleBlock,
  label = "Customize view",
  className,
}: CustomizeViewMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on any click landing outside the trigger and its panel.
  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        onToggleOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, onToggleOpen]);

  return (
    <div ref={rootRef} className={classNames(styles.wrap, className)}>
      <button
        type="button"
        aria-expanded={open}
        className={styles.trigger}
        onClick={() => onToggleOpen(!open)}
      >
        <TuneIcon className={styles.triggerIcon} />
        {label}
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.panelTitle}>Blocks kept in view</div>
          {blocks.map((block) => (
            <label key={block.key} className={styles.option}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={block.visible}
                onChange={(event) =>
                  onToggleBlock(block.key, event.target.checked)
                }
              />
              {block.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default CustomizeViewMenu;
